import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, API_BASE } from '../utils/api';
import { ORDER_STATUSES } from '../constants/statuses';
import { STATUS_LABELS } from '../constants/colors';
import { getOrderNumber } from '../utils/orderTitle';
import { deliveredTickNotice, receiptClearNotice } from '../utils/deliveredTickNotice';
import { MENU_SECTION_HEADING_TEXT } from './OrderActionsMenu';

// 🔻 `FORWARD_SEQUENCE` AND `isBackwardJump` ARE DELETED (2026-09-19), NOT
//    MOVED — AND THE REASON IS THE WHOLE LESSON OF THIS CHANGE.
//
// They were `['submitted', 'approved', 'ordered', 'partially_delivered',
// 'delivered']` and an index comparison over it, and they decided which jumps
// got a side-state warning. **A direction test built on a sequence answers
// `false` for any value absent from that sequence** — `rejected` and
// `cancelled` were never in it — so every jump OUT of those two was
// indistinguishable from a forward jump, and any enumeration built on the
// predicate under-counts by exactly the statuses it cannot see. Six of the nine
// silently-unwarned pairs were of that shape, and they are the likeliest real
// ones: an order cancelled or rejected after Stores had already ticked items.
//
// 🚨 **NEVER DECIDE WHETHER TO WARN FROM A DIRECTION TEST.** The server's arm
// is keyed on the TARGET STATUS and on nothing else, so the warning must be
// too. That is `TARGETS_CLEARING_ITEM_RECEIPTS` below, which is a mirror of the
// server's own list and therefore cannot under-count by construction. If a
// direction test is ever genuinely needed here again, write it over
// `ORDER_STATUSES` and make the absent-value case explicit.

// ═══════════════════════════════════════════════════════════════════════════
// 🔑 THE TARGETS THAT CLEAR THE DELIVERED TICKS — a MIRROR of the server's
//    `TARGETS_NEEDING_ITEM_RESET` (`server/routes/orders.js`), a convenience
//    and NEVER a gate. The server decides; this decides only whether to warn.
// ═══════════════════════════════════════════════════════════════════════════
// 🚨 KEYED ON THE TARGET ALONE, DELIBERATELY — NO DIRECTION TEST. The warning
// must fire on every path that clears receipts, and direction is not what
// decides that: `reset_side_state` is sent on every jump from this control, so
// the server's arm fires whenever the target is in this list and differs from
// the current status, forward or backward or neither.
//
// 🔻 MEASURED, 2026-09-19 — 24 status pairs reach the wipe and NINE of them
// showed no warning at all: `submitted → approved`, `submitted → ordered`,
// `approved → ordered`, and — the six that matter most — every jump out of
// `rejected` or `cancelled` into `submitted`/`approved`/`ordered`. Those six
// are reachable exactly when Stores has already ticked items and the order was
// then cancelled or rejected, i.e. the case with a real receipt to lose. They
// were invisible because the old branch asked `isBackwardJump`, which answers
// `false` for a status it has never heard of.
//
// 🪤 `cancelled` is NOT here and must not be: it is not in the server's list,
// and as a TARGET it never reaches this component's PATCH at all (it is
// delegated to the parent's cancel form below).
const TARGETS_CLEARING_ITEM_RECEIPTS = ['submitted', 'approved', 'rejected', 'ordered'];

function clearsItemReceipts(from, to) {
  return to !== from && TARGETS_CLEARING_ITEM_RECEIPTS.includes(to);
}

/**
 * Inline jump-to-stage dropdown for an order's status. Listed in the expanded
 * row of DepartmentOrders next to InlinePoNumberEditor. The parent gates
 * visibility; this component knows nothing about roles.
 *
 * ⚠️ CORRECTED 2026-08-06. This said "Buyer/admin only … the underlying server
 * endpoint already 403s for non-admin/non-buyer." That has not been true since
 * tranche 3 increment 3c: `PATCH /api/orders/:id/status` is
 * `requireDepartmentCapability(req, res, order.department_id,
 * 'canManageDepartmentOrders')` (server/routes/orders.js), resolved IN THE
 * ORDER'S OWN DEPARTMENT. The stale comment mattered — DepartmentOrders was
 * gating this control on the unscoped `isBuyer`, so a department buyer with no
 * `user_roles` row never saw a dropdown the server would have accepted. Parents
 * now use `useOrderAuthority().canManageOrder`.
 *
 * Always sends `reset_side_state: true`, so a jump to any of
 * `submitted`/`approved`/`rejected`/`ordered` clears the delivered ticks and a
 * jump to `submitted` additionally resets the approval chain (see the server
 * cleanup at server/routes/orders.js PATCH /:id/status).
 *
 * 🔻 CORRECTED 2026-09-19. This paragraph ended *"Forward jumps are no-ops for
 * the reset branch — items haven't been received in a freshly forward-jumped
 * state anyway."* **Both halves are false.** The server's arm is keyed on the
 * TARGET STATUS and nothing else, so a forward jump into that list runs the
 * identical wipe; and items very much can be received on a pre-delivery status,
 * because `pages/OrderPage.js`'s status `<select>` moves an order without
 * sending `reset_side_state` and strands the ticks where they sit. The sentence
 * was load-bearing — it is the reason nine wipe-carrying pairs shipped with no
 * warning at all.
 *
 * 🔑 The tick is cleared; the RECEIPT is not. `received_by` / `received_date`
 * survive on the row and the audit line names who was overridden (owner ruling,
 * 2026-09-19) — which is why the warning below says so rather than implying the
 * evidence is destroyed.
 *
 * Special cases:
 * - `cancelled` → delegated to the parent's existing cancel form via
 *   `onCancelClick(order)`. We never PATCH /status with cancelled here.
 * - `rejected` → prompts for `rejection_reason` (server-required).
 * - target === current → no-op.
 *
 * Props:
 * - `order` — order object (id, status, order_number).
 * - `onSaved(updatedOrder)` — fires after a successful PATCH so the parent can
 *   sync its row state without a full refetch.
 * - `onCancelClick(order)` — called when the user picks `cancelled`. Parent
 *   should open its existing cancel form (the row's Cancel button uses it too).
 * - `testId` — `data-testid` for the select. Differ per page.
 * - `requestRejectionReason(order)` — OPTIONAL, added 2026-10-03.
 *   `=> Promise<string|null>`. Called when the user picks `rejected`, to collect
 *   the server-required reason in the parent's own `OrderReasonDialog` instead
 *   of a `window.prompt`. Omit it and the component falls back to the prompt, so
 *   every existing caller is byte-identical. Same shape, and the same structural
 *   reason, as `requestOverrideReason` below.
 * - `requestOverrideReason(order, targetStatus)` — OPTIONAL, added 2026-09-13.
 *   `=> Promise<string|null>`. Called ONLY when the server has answered
 *   `400 status_jump_reason_required`, to collect the mandatory reason. Omit it
 *   and the component falls back to the `window.prompt` it has always used, so
 *   every existing caller is byte-identical. See `handleChange` for why the
 *   PARENT owns the dialog rather than this component.
 */
export default function InlineStatusJump({
  order, onSaved, onCancelClick, testId, requestOverrideReason, requestRejectionReason,
  // Visible heading; Dept Orders' phone menu passes "Change status" (owner
  // 2026-10-04 — "override" read like approving). Omitted → "Override order".
  label,
}) {
  const savedStatus = order.status || 'submitted';
  const [displayed, setDisplayed] = useState(savedStatus);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inFlightRef = useRef(false);

  // Keep the select in sync if the parent refetches and status flips externally
  // (e.g. via the existing per-row action buttons or an approval coming in).
  useEffect(() => {
    setDisplayed(savedStatus);
  }, [savedStatus]);

  const revert = () => setDisplayed(savedStatus);

  const handleChange = async (e) => {
    const target = e.target.value;
    if (inFlightRef.current) return;
    if (!target || target === savedStatus) {
      revert();
      return;
    }
    setDisplayed(target);
    setError('');

    // Cancelled is its own flow with a reason form. Delegate, then revert the
    // select so it goes back to the saved status until the parent re-fetches
    // and shows the cancelled state itself.
    if (target === 'cancelled') {
      if (typeof onCancelClick === 'function') onCancelClick(order);
      revert();
      return;
    }

    // Confirmation. 🔻 REWORDED 2026-09-19 — it said *"Backward jumps surface
    // the side-state warning"*, which described the defect rather than the
    // intent. The rule is: **every jump that clears receipts warns, whatever
    // direction it is**, because `reset_side_state` goes with every PATCH from
    // this control and the server's arm reads the target alone.
    const fromLabel = STATUS_LABELS[savedStatus] || savedStatus;
    const toLabel = STATUS_LABELS[target] || target;
    const isReject = target === 'rejected';
    // The order NUMBER (2026-08-12); a confirmation prompt is read by a human.
    let msg = `Change order ${getOrderNumber(order)} status from "${fromLabel}" to "${toLabel}"?`;
    if (target === 'delivered') {
      // 🔑 THE OWNER ASKED FOR THIS SENTENCE BY NAME (2026-09-12): *"The warning
      // for the override should tell the user that moving the order to delivered
      // will tick all items off as being delivered"*. The server now does it —
      // `PATCH /:id/status` marks every not-already-received item received, with
      // the caller as `received_by` — so this warning describes a write that
      // really happens, which is the only kind worth showing.
      //
      // 🪤 THE WORDING AND THE COUNT LIVE IN `utils/deliveredTickNotice.js`, not
      // here — `OrderPage.js` has TWO more confirms that reach `delivered`, and
      // three copies of one promise is how two of them keep the old wording
      // after the next retune. Both call sites here pass the EXPANDED ROW'S
      // `detail` (`DepartmentOrders.js` and `MyOrders.js`, both `order={detail}`),
      // i.e. the body of `GET /api/orders/:id`, so the items array is present.
      //
      // ⚠️ `delivered` CANNOT ALSO REACH THE CLEAR-RECEIPTS BRANCH BELOW, and
      // since 2026-09-19 that is guaranteed by a LIST rather than by an index
      // comparison: `delivered` is not in `TARGETS_CLEARING_ITEM_RECEIPTS` (it
      // is not in the server's `TARGETS_NEEDING_ITEM_RESET` either — the server
      // TICKS on this target, it does not clear). This branch therefore
      // REPLACES nothing and the two can never both apply. The old argument
      // rested on `delivered` being last in `FORWARD_SEQUENCE`, which is gone.
      msg += `\n\n${deliveredTickNotice(order)}`;
    } else if (clearsItemReceipts(savedStatus, target)) {
      // ═══════════════════════════════════════════════════════════════════
      // 🔻 ONE BRANCH REPLACES TWO (2026-09-19), AND THE REPLACEMENT IS
      //    WIDER THAN BOTH OF THEM PUT TOGETHER.
      // ═══════════════════════════════════════════════════════════════════
      // What was here: an `isBackward` arm promising *"clear all
      // item-delivered flags and reset the approval chain to a fresh state"*
      // and an `isReject` arm promising *"clear all item-delivered flags"*.
      // Three faults, all measured:
      //   1. COVERAGE — between them they covered 15 of the 24 pairs that
      //      reach the wipe. The nine they missed are enumerated at
      //      `TARGETS_CLEARING_ITEM_RECEIPTS` above.
      //   2. TRUTH — the chain half was false or unprovable on 9 of 9 backward
      //      wipes (only 4 target `submitted`, and even those need a
      //      `department_id` and an `order_approvals` row). It is now appended
      //      on the one target where it can fire, and nowhere else.
      //   3. WORDING — *"item-delivered flags"* names a column, not the thing
      //      being destroyed. The user is deleting somebody's record that the
      //      goods arrived; the sentence now says that, and says the record
      //      itself survives in the log.
      //
      // 🪤 `delivered` CANNOT REACH HERE and the ordering is what guarantees
      // it: `delivered` is not in `TARGETS_CLEARING_ITEM_RECEIPTS`, and its
      // own branch is tested first regardless. The two can never both apply.
      msg += `\n\n${receiptClearNotice(order)}`;
      // 🔑 THE CHAIN CLAUSE, ON THE ONE TARGET WHOSE ARM CAN FIRE. Worded as
      // what the user is asking for rather than as a promise about rows: the
      // client cannot see whether an `order_approvals` row exists (it is not on
      // the list row or the detail payload), and a My Space order has none, so
      // *"the approval chain WILL be reset"* would be the old lie in a new
      // place. "Back into the approval queue" is true of every order that has a
      // queue and vacuous — not false — for one that does not.
      if (target === 'submitted') {
        msg += ' The order goes back into the approval queue from the first step.';
      }
      if (isReject) msg += ' A rejection reason is required next.';
    }
    if (!window.confirm(msg)) {
      revert();
      return;
    }

    let rejectionReason;
    if (isReject) {
      // 🔀 2026-10-03 — THE PARENT'S IN-PAGE DIALOG WHEN IT SUPPLIES ONE,
      // otherwise the `window.prompt` this has always used, so a caller that
      // passes no handler is byte-identical. Same prop shape, and the same
      // reason for it, as `requestOverrideReason` below: this component renders
      // inside `OrderActionsMenu`'s panel, `ConfirmDialog` is a bare
      // `fixed inset-0` overlay with no portal, and `ui/Dropdown` closes on an
      // outside `mousedown` — so a dialog mounted here would be unmounted by
      // the user's first click in it. Asking upward keeps it outside the panel.
      // This component may itself unmount while the promise is pending
      // (the dropdown closes behind the overlay); harmless, for the reason
      // written out at `requestOverrideReason` — the continuation holds what it
      // needs in its closure and React 18 makes a `setState` on an unmounted
      // component a no-op.
      rejectionReason = typeof requestRejectionReason === 'function'
        ? await requestRejectionReason(order)
        : window.prompt('Enter rejection reason:');
      // 🪤 The emptiness test is UNCHANGED and still belongs here. The dialog's
      // own Confirm gate already refuses a blank REJECT reason, so this can
      // only fire on the prompt fallback or a dismissal — but it is the only
      // thing standing between a withdrawn gesture and a PATCH, and the server
      // requires the field, so it stays whatever the source.
      if (!rejectionReason || !rejectionReason.trim()) {
        revert();
        return;
      }
      rejectionReason = rejectionReason.trim();
    }

    inFlightRef.current = true;
    setSaving(true);
    try {
      const body = { status: target, reset_side_state: true };
      if (rejectionReason) body.rejection_reason = rejectionReason;
      const patchStatus = () => apiFetch(`${API_BASE}/api/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let res = await patchStatus();

      // 🚨 A FORWARD JUMP THAT SKIPS A PENDING APPROVAL STEP DEMANDS A REASON
      // (2026-09-01, server/routes/orders.js). The client asks only when the
      // server actually refuses — it has no reliable way to know client-side
      // whether the order's chain still has a VISIBLE pending step (the list
      // row's `approval_info` is only attached while `status === 'submitted'`,
      // i.e. it goes dark exactly when a SECOND forced jump would need this
      // check most), so this reacts to the structured refusal rather than
      // guessing. Same `window.prompt` convention as the rejection-reason
      // case just above.
      if (res.status === 400) {
        const errBody = await res.json().catch(() => ({}));
        if (errBody.code === 'status_jump_reason_required') {
          // 🔀 **2026-09-13 — THE REASON IS COLLECTED IN THE SHARED
          //    `SkipApprovalsDialog` WHEN THE PARENT SUPPLIES ONE**, so this
          //    door and the `Skip remaining approvals…` menu item ask the same
          //    question in the same words. `window.prompt` remains the fallback
          //    for a caller that passes no handler. ⚠️ **That used to read
          //    "(`MyOrders.js` today)" and is STALE since 2026-10-03** — both
          //    order-expansion hooks now pass this prop AND the new
          //    `requestRejectionReason`, so no live caller takes either
          //    fallback. The fallbacks stay because the component must keep
          //    working for a caller that supplies neither.
          //
          // 🚨 **THE PARENT OWNS THE DIALOG, NOT THIS COMPONENT, AND THAT IS
          //    STRUCTURAL.** This renders inside `OrderActionsMenu`'s panel;
          //    `ConfirmDialog` is a bare `fixed inset-0` overlay with NO portal
          //    and `ui/Dropdown` closes on an outside `mousedown`, so a dialog
          //    mounted here would be unmounted by the user's first click in it.
          //    Asking upward keeps the dialog outside the panel. This component
          //    may itself unmount while the promise is pending (the dropdown
          //    closes behind the overlay) — harmless: the continuation below
          //    holds `patchStatus` and `onSaved` in its closure, and React 18
          //    makes a `setState` on an unmounted component a no-op.
          //
          // 🔴 **IT STILL REACTS TO THE SERVER'S REFUSAL RATHER THAN
          //    PREDICTING IT, AND THAT IS DELIBERATE — A PRE-EMPTIVE DIALOG
          //    WOULD BE WRONG FOR TWO REAL CASES THE CLIENT CANNOT SEE.**
          //    `PATCH /api/orders/:id/status` demands a reason only when a
          //    VISIBLE pending step exists AND the caller is not the owner of
          //    their own My Space order (the 2026-09-11 *"Approve is a real
          //    click"* exemption). The client knows neither: `approval_info`
          //    does not distinguish hidden bookkeeping steps from real ones,
          //    and personal-space ownership needs `productions.is_personal` +
          //    `owner_user_id`, which never reach the row. Predicting would
          //    demand a written justification from a My Space owner for
          //    skipping a step the server itself calls a bug's residue.
          const overrideReason = typeof requestOverrideReason === 'function'
            ? await requestOverrideReason(order, target)
            : window.prompt(
              'This will jump the order past a pending approval step. Enter a reason:'
            );
          if (!overrideReason || !overrideReason.trim()) {
            revert();
            return;
          }
          body.reason = overrideReason.trim();
          res = await patchStatus();
        } else {
          throw new Error(errBody.error || `Failed (${res.status})`);
        }
      }

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || `Failed (${res.status})`);
      }
      const updated = await res.json();
      if (typeof onSaved === 'function') onSaved(updated);
      // displayed already matches; nothing else to do — parent will refetch or
      // re-sync via the effect above when `order.status` changes.
    } catch (err) {
      console.error('Status jump failed:', err);
      setError(err.message || 'Failed to change status');
      revert();
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  /*
   * ═══════════════════════════════════════════════════════════════════════
   * 🔀 STACKED — LABEL ABOVE THE SELECT (2026-08-12). IT WAS A SIDE-BY-SIDE
   *    ROW AND THAT ROW OVERFLOWED ITS PANEL.
   * ═══════════════════════════════════════════════════════════════════════
   * Product owner, reporting it from staging: *"action menu for the whole
   * order still has 'verride:' clipped. it doesnt fit"*, then the fix:
   * *"put the 'Overide order' above the dropdown"*.
   *
   * 🚨 **THE CLIPPING WAS ON THE LEFT, WHICH IS THE DIAGNOSTIC — ordinary
   * truncation eats the RIGHT.** The old row was
   * `flex items-center gap-2 justify-end` with a `w-32 flex-shrink-0` label.
   * Since 2026-08-11 this renders as the `Status` section's FOOTER inside
   * `OrderActionsMenu`'s panel, which is `w-64 p-1` — so 128px of unshrinkable
   * label + 8px gap + a `<select>` whose intrinsic width is set by its widest
   * OPTION (`Cancelled (current)`) exceeded the panel. With `justify-end` the
   * overflow goes to the inline START, so the label's first character was
   * pushed outside the panel's left edge and `Status Override:` rendered as
   * `verride:`. **Nothing was truncated; the row was simply wider than its box.**
   *
   * 🔑 **STACKING REMOVES THE CONTEST RATHER THAN WINNING IT** — no label
   * competing with a control for one row, and it matches how every other
   * labelled control in the app is built.
   *
   * 🪤 **`w-full` ALONE IS NOT ENOUGH ON THE SELECT AND `min-w-0` IS THE ONE
   * THAT GETS FORGOTTEN.** A flex item's default `min-width: auto` pins it at
   * its intrinsic content width — here the width of `Cancelled (current)` —
   * so it would refuse to shrink and overflow the panel even stacked. This
   * repo already records that as *"the commonest silent cause"* of exactly
   * this, at `constants/inputs.js`.
   *
   * 📝 **THE LABEL IS `Override order`, NOT `Status Override:`** — the owner's
   * own wording, and the trailing colon is dropped deliberately: a colon reads
   * as a field prefix for something on the SAME line, which is no longer true.
   * ⚠️ It is the only text identifying the control that is the sole rescue for
   * an order stranded by an offboarded approver, so it must stay visible IN
   * FULL — never truncate it, never hide it to win width.
   *
   * 🎨 **THE LABEL'S WEIGHT IS `MENU_SECTION_HEADING_TEXT`, NOT ITS OWN
   * `font-medium` STYLE — owner ruling 2026-09-13.** Product owner: *"'Override
   * order' in the action menu text is much bigger and bolder … it looks like
   * its the natural place to go for users"*, comparing it to a plain group
   * heading like "Next Step". It is imported from `./OrderActionsMenu` rather
   * than restated so it can never drift back to out-weighing the group
   * headings above it in the same panel.
   *
   * 🔒 `aria-label="Jump to status"` is UNCHANGED and is deliberately not
   * merged with the visible label: it is the select's accessible name and is
   * asserted by existing tests. The visible text is not wired via `htmlFor`
   * because this component has no stable id to point at — see the 54
   * id-referencing sites problem recorded for `OrderForm` in
   * `client/src/pages/CLAUDE.md`.
   */
  return (
    <span className="flex flex-col gap-1 min-w-0" onClick={(e) => e.stopPropagation()}>
      <span className={MENU_SECTION_HEADING_TEXT}>{label || 'Override order'}</span>
      <select
        value={displayed}
        onChange={handleChange}
        disabled={saving}
        className="w-full min-w-0 px-2 py-0.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        data-testid={testId}
        aria-label="Jump to status"
      >
        {/*
          * 🎨 **THE CURRENT STATUS IS HIGHLIGHTED (2026-08-12).** Product owner:
          * *"just highlight in the override dropdown the current order status so
          * the user instinctively knows to click one above or one below to
          * increment or decrement the order status"*.
          *
          * 🚨 **THE `(current)` TEXT IS THE LOAD-BEARING CHANNEL AND THE COLOUR
          * IS AN ENHANCEMENT ON TOP — NOT THE OTHER WAY AROUND. Do not remove
          * the text in favour of the styling.** Styling an `<option>` is barely
          * supported and varies by platform: Chromium and Firefox on
          * Windows/Linux honour `background-color` and `font-weight`, while
          * **Safari/macOS and every mobile picker render the list natively and
          * ignore both.** So on those platforms the text is the ONLY signal, and
          * it is the one that has always been there.
          * ⚠️ **This is therefore a PARTIAL delivery of the request and is
          * flagged as such** — a universally reliable visual marker would have
          * to live in the option's TEXT (a leading glyph), which changes a
          * string the owner did not ask to change. Offered, not taken.
          * 🔴 **Do NOT "solve" this with a custom listbox** — that is a far
          * bigger change than was asked for and would need its own keyboard,
          * focus and screen-reader work.
          */}
        {ORDER_STATUSES.map((s) => (
          <option
            key={s}
            value={s}
            className={s === savedStatus
              ? 'font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
              : undefined}
          >
            {STATUS_LABELS[s] || s}{s === savedStatus ? ' (current)' : ''}
          </option>
        ))}
      </select>
      {saving && <span className="text-xs text-gray-500 dark:text-gray-400">Saving…</span>}
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </span>
  );
}
